import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { markReadResponseSchema, notificationsResponseSchema, type Notification } from '@unit/shared';
import { useApi } from '../../lib/useApi.js';
import { apiPost } from '../../lib/api.js';
import { useAction } from '../../lib/useAction.js';
import { ApiError } from '../../components/ApiError.js';
import { ActionError } from '../../components/ActionError.js';
import { formatDate } from '../issuance/FormsPage.js';
import { NOTIFICATIONS_CHANGED } from './NotificationsBell.js';

/**
 * The caller's notifications (REQUIREMENTS §2.4). `title` and `body` are
 * i18n keys with `params`, rendered here in the reader's language. Opening
 * one marks it read and follows its link.
 */
export function NotificationsPage() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const state = useApi('/notifications', notificationsResponseSchema);
  const markAll = useAction(() => apiPost('/notifications/read', markReadResponseSchema, { ids: 'all' }));
  const markOne = useAction((id: string) => apiPost('/notifications/read', markReadResponseSchema, { ids: [id] }));

  if (state.kind === 'loading') return <p className="text-slate-500">{t('home.loading')}</p>;
  if (state.kind === 'error') return <ApiError code={state.code} requestId={state.requestId} />;

  const { notifications, unread } = state.data;

  const changed = () => window.dispatchEvent(new Event(NOTIFICATIONS_CHANGED));

  const open = async (n: Notification) => {
    if (!n.read) {
      await markOne.run(n.id);
      changed();
    }
    navigate(n.linkTo || '/');
  };

  // Interpolated values that are themselves enum-ish (a report kind, an
  // outcome, a rotation reason) are translated before they go in.
  const params = (n: Notification): Record<string, string | number> => {
    const out: Record<string, string | number> = { ...n.params };
    if (typeof out.kind === 'string') out.kind = t(`gear.${out.kind}`);
    if (typeof out.outcome === 'string') out.outcome = t(`assignmentStatus.${out.outcome}`, { defaultValue: out.outcome });
    if (typeof out.reason === 'string') out.reason = t(`gear.rotationReasons.${out.reason}`, { defaultValue: out.reason });
    return out;
  };

  return (
    <section className="rounded-2xl bg-white p-5 shadow-sm ring-1 ring-slate-200" data-testid="notifications-page">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h2 className="text-lg font-semibold text-slate-900">{t('notifications.title')}</h2>
        {unread > 0 && (
          <button
            type="button"
            onClick={() => void markAll.run().then((r) => { if (r) { changed(); state.reload(); } })}
            disabled={markAll.busy}
            className="rounded-lg px-3 py-1.5 text-sm text-slate-700 ring-1 ring-slate-300 hover:bg-slate-100 disabled:opacity-40"
            data-testid="notifications-mark-all"
          >
            {t('notifications.markAllRead', { count: unread })}
          </button>
        )}
      </div>
      <ActionError error={markAll.error ?? markOne.error} />
      {notifications.length === 0 && <p className="mt-3 text-sm text-slate-500" data-testid="notifications-empty">{t('notifications.empty')}</p>}
      <ul className="mt-3 divide-y divide-slate-100">
        {notifications.map((n) => (
          <li key={n.id}>
            <button
              type="button"
              onClick={() => void open(n)}
              className={`flex w-full flex-wrap items-start justify-between gap-2 py-3 text-start transition hover:bg-slate-50 ${n.read ? 'text-slate-500' : 'text-slate-900'}`}
              data-testid="notification-row"
              data-read={n.read}
              data-type={n.type}
            >
              <span>
                <span className={`block ${n.read ? '' : 'font-semibold'}`}>{t(n.title, { defaultValue: n.type })}</span>
                <span className="block text-sm">{t(n.body, { ...params(n), defaultValue: '' })}</span>
              </span>
              <span className="latin-inline text-xs text-slate-400">{formatDate(n.createdAt, i18n.language)}</span>
            </button>
          </li>
        ))}
      </ul>
    </section>
  );
}
